import React, { useEffect, useState } from 'react';
import {
  Modal,
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import Toast from 'react-native-toast-message';
import { fpColors } from '../../../styles/theme';
import { PrimaryCTA } from '../../../components/PrimaryCTA';
import { api } from '../../../api/remembrApi';
import type { InventoryItemResponse } from '../../../api/types';

/**
 * ConditionEditSheet — Stage 2D polish.
 *
 * Replaces the redundant APPROX. VALUE bento cell with an editable
 * CONDITION cell. 5-chip picker writes to `item.condition`, which
 * the backend already accepts on `InventoryItemUpdateRequest`.
 *
 * Condition matters for:
 *  - Lending decisions ("Pen is Damaged → don't lend")
 *  - Insurance valuation (Like-New worth more than Fair)
 *  - Replacement planning (Poor → buy a new one)
 */

export const CONDITION_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
  description: string;
  /** 1-5 star rating used for the chip visual hierarchy. */
  stars: number;
  /** Color tier — green = great, amber = ok, red = bad. */
  tier: 'great' | 'good' | 'fair' | 'poor' | 'bad';
}> = [
  {
    value: 'like_new',
    label: 'Like new',
    description: 'No visible wear; original packaging or mint condition.',
    stars: 5,
    tier: 'great',
  },
  {
    value: 'good',
    label: 'Good',
    description: 'Light wear; fully functional.',
    stars: 4,
    tier: 'good',
  },
  {
    value: 'fair',
    label: 'Fair',
    description: 'Visible wear but works as expected.',
    stars: 3,
    tier: 'fair',
  },
  {
    value: 'poor',
    label: 'Poor',
    description: 'Significant wear; reduced function.',
    stars: 2,
    tier: 'poor',
  },
  {
    value: 'damaged',
    label: 'Damaged',
    description: 'Broken or non-functional.',
    stars: 1,
    tier: 'bad',
  },
];

const TIER_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  great: { bg: '#1B8E3E20', text: '#0E5E29', dot: '#1B8E3E' },
  good: { bg: '#2EA85B20', text: '#1B6F3D', dot: '#2EA85B' },
  fair: { bg: '#F2A02D20', text: '#7A4F0C', dot: '#F2A02D' },
  poor: { bg: '#E5762620', text: '#7A3D0C', dot: '#E57626' },
  bad: { bg: '#D63B3B20', text: '#7A1A1A', dot: '#D63B3B' },
};

interface ConditionEditSheetProps {
  visible: boolean;
  item: InventoryItemResponse;
  onClose: () => void;
  onSaved: (updated: InventoryItemResponse) => void;
}

export const ConditionEditSheet: React.FC<ConditionEditSheetProps> = ({
  visible,
  item,
  onClose,
  onSaved,
}) => {
  const initialCondition = (item as any).condition as string | undefined;
  const [selected, setSelected] = useState<string | null>(
    initialCondition && CONDITION_OPTIONS.find((c) => c.value === initialCondition)
      ? initialCondition
      : null,
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setSelected(
        initialCondition && CONDITION_OPTIONS.find((c) => c.value === initialCondition)
          ? initialCondition
          : null,
      );
      setError(null);
    }
  }, [visible, initialCondition]);

  const handleSave = async () => {
    if (!item.id) {
      onClose();
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const updated = await api.updateInventoryItem(item.id, {
        // Backend's UpdateInventoryItemRequest accepts `condition: str`.
        // Send the lowercased enum value (or empty string to clear).
        condition: selected ?? '',
      });
      const merged: InventoryItemResponse = {
        ...item,
        ...updated,
        condition: selected ?? undefined,
      } as InventoryItemResponse;
      Toast.show({ type: 'success', text1: 'Condition saved' });
      onSaved(merged);
      onClose();
    } catch (err: any) {
      const detailRaw = err?.response?.data?.detail;
      const detail = Array.isArray(detailRaw)
        ? detailRaw.map((d: any) => d?.msg || JSON.stringify(d)).join(', ')
        : typeof detailRaw === 'string'
          ? detailRaw
          : null;
      setError(detail || err?.message || 'Could not save condition.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View
          style={styles.sheet}
          accessible
          accessibilityViewIsModal
          accessibilityLabel="Update condition"
          // @ts-ignore — RN Web only
          role="dialog"
          aria-modal={true}
          testID="condition-edit-sheet"
        >
          <View style={styles.header}>
            <Text style={styles.title}>Update condition</Text>
            <Pressable
              onPress={onClose}
              hitSlop={8}
              accessibilityLabel="Close"
              accessibilityRole="button"
              style={styles.closeBtn}
              testID="condition-edit-close"
            >
              <MaterialIcons name="close" size={22} color={fpColors.onSurface} />
            </Pressable>
          </View>

          <View style={styles.body}>
            <Text style={styles.fieldLabel}>
              What&apos;s the condition of &ldquo;{item.item_name ?? 'this item'}&rdquo;?
            </Text>
            <View style={styles.optionList}>
              {CONDITION_OPTIONS.map((opt) => {
                const isSelected = selected === opt.value;
                const palette = TIER_COLORS[opt.tier];
                return (
                  <Pressable
                    key={opt.value}
                    onPress={() => setSelected(opt.value)}
                    accessibilityRole="radio"
                    accessibilityState={{ selected: isSelected }}
                    accessibilityLabel={`${opt.label} — ${opt.description}`}
                    testID={`condition-edit-option-${opt.value}`}
                    style={({ pressed }) => [
                      styles.option,
                      isSelected && {
                        borderColor: palette.dot,
                        backgroundColor: palette.bg,
                      },
                      pressed && { opacity: 0.85 },
                    ]}
                  >
                    <View style={[styles.dot, { backgroundColor: palette.dot }]} />
                    <View style={{ flex: 1 }}>
                      <View style={styles.optionTitleRow}>
                        <Text style={styles.optionLabel}>{opt.label}</Text>
                        <Text style={styles.optionStars}>
                          {'★'.repeat(opt.stars)}
                          <Text style={{ color: fpColors.outlineVariant }}>
                            {'★'.repeat(5 - opt.stars)}
                          </Text>
                        </Text>
                      </View>
                      <Text style={styles.optionDesc} numberOfLines={2}>
                        {opt.description}
                      </Text>
                    </View>
                    {isSelected ? (
                      <MaterialIcons
                        name="check-circle"
                        size={18}
                        color={palette.dot}
                      />
                    ) : (
                      <View style={styles.optionRadioPlaceholder} />
                    )}
                  </Pressable>
                );
              })}
            </View>

            {error ? (
              <View style={styles.errorBanner} testID="condition-edit-error">
                <MaterialIcons
                  name="error-outline"
                  size={16}
                  color={fpColors.error}
                />
                <Text style={styles.errorText}>{error}</Text>
              </View>
            ) : null}
          </View>

          <View style={styles.footer}>
            <Pressable
              onPress={onClose}
              hitSlop={8}
              accessibilityRole="button"
              accessibilityLabel="Cancel"
              style={styles.cancelBtn}
              testID="condition-edit-cancel"
            >
              <Text style={styles.cancelText}>Cancel</Text>
            </Pressable>
            <View style={styles.saveBtnWrap} testID="condition-edit-save">
              <PrimaryCTA
                label={saving ? 'Saving…' : 'Save'}
                onPress={handleSave}
                disabled={saving}
              />
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
};

/** Public helper — given a backend `condition` string, returns the
 *  matched option (label + tier + stars). Used by the bento cell to
 *  render the display value consistently with the picker. */
export function describeCondition(
  value: string | null | undefined,
): { label: string; tier: string; stars: number } | null {
  if (!value) return null;
  const match = CONDITION_OPTIONS.find((c) => c.value === value);
  if (!match) return null;
  return { label: match.label, tier: match.tier, stars: match.stars };
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(28, 23, 33, 0.55)',
    justifyContent: 'flex-end',
  },
  sheet: {
    backgroundColor: fpColors.surface,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingBottom: Platform.OS === 'ios' ? 24 : 16,
    maxHeight: '92%',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingTop: 18,
    paddingBottom: 14,
    borderBottomWidth: 1,
    borderBottomColor: fpColors.outlineVariant,
  },
  title: {
    fontFamily: 'Inter_700Bold',
    fontSize: 18,
    color: fpColors.onSurface,
  },
  closeBtn: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 16,
  },
  body: { padding: 20, gap: 12 },
  fieldLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 13,
    color: fpColors.onSurface,
  },
  optionList: { gap: 8 },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: fpColors.outlineVariant,
    backgroundColor: fpColors.surfaceContainerLow,
  },
  dot: { width: 10, height: 10, borderRadius: 5 },
  optionTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  optionLabel: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurface,
  },
  optionStars: { fontSize: 13, color: fpColors.tertiary, letterSpacing: 1 },
  optionDesc: {
    fontFamily: 'Inter_400Regular',
    fontSize: 11,
    color: fpColors.onSurfaceVariant,
    marginTop: 2,
  },
  optionRadioPlaceholder: { width: 18, height: 18 },
  errorBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 10,
    backgroundColor: `${fpColors.errorContainer}40`,
    borderRadius: 10,
    marginTop: 8,
  },
  errorText: {
    flex: 1,
    fontFamily: 'Inter_500Medium',
    fontSize: 12,
    color: fpColors.error,
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 14,
    borderTopWidth: 1,
    borderTopColor: fpColors.outlineVariant,
  },
  cancelBtn: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 12,
  },
  cancelText: {
    fontFamily: 'Inter_700Bold',
    fontSize: 14,
    color: fpColors.onSurfaceVariant,
  },
  saveBtnWrap: { flex: 1 },
});

export default ConditionEditSheet;
